import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { format, isSameMonth, isToday, isPast } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, Clock, MapPin, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CalendarEvent } from './types';

/* ── Tabs ─────────────────────────────────────────────────────────────────── */

type Tab = 'upcoming' | 'pending' | 'recurring' | 'past' | 'cancelled';

const TABS: { id: Tab; label: string }[] = [
  { id: 'upcoming',  label: 'Próximas'    },
  { id: 'pending',   label: 'Pendientes'  },
  { id: 'recurring', label: 'Recurrentes' },
  { id: 'past',      label: 'Pasadas'     },
  { id: 'cancelled', label: 'Canceladas'  },
];

function filterByTab(events: CalendarEvent[], tab: Tab): CalendarEvent[] {
  return events
    .filter(ev => !(ev as any)._isBloqueHorario)
    .filter(ev => {
      const cita = (ev as any)._cita;
      const estado: string = cita?.estado ?? '';
      const start = new Date(ev.start);
      switch (tab) {
        case 'upcoming':
          return !isPast(start) && !['cancelada', 'completada', 'no_asistio'].includes(estado);
        case 'pending':
          return estado === 'programada';
        case 'recurring':
          return !!cita?.esRecurrente;
        case 'past':
          return isPast(start) && estado !== 'cancelada';
        case 'cancelled':
          return estado === 'cancelada';
      }
    })
    .sort((a, b) => {
      const diff = new Date(a.start).getTime() - new Date(b.start).getTime();
      return tab === 'past' ? -diff : diff;
    });
}

function initials(name: string): string {
  return name.split(' ').map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
}

/* ── Event card ───────────────────────────────────────────────────────────── */

function EventCard({ ev, onEventClick }: { ev: CalendarEvent; onEventClick?: (e: CalendarEvent) => void }) {
  const cita  = (ev as any)._cita;
  const start = new Date(ev.start);
  const end   = new Date(ev.end);
  const today = isToday(start);

  const location = cita?.tipo === 'teleconsulta'
    ? 'Teleconsulta'
    : cita?.clinica?.nombre ?? '';

  const doctor: string  = cita?.medico?.nombre ?? '';
  const estado: string  = cita?.estado ?? '';
  const isCancelled     = estado === 'cancelada';

  return (
    <div
      onClick={() => onEventClick?.(ev)}
      role="button"
      tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onEventClick?.(ev)}
      className={cn(
        'mx-4 my-2 rounded-2xl border border-gray-200 bg-white flex items-stretch',
        'cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all select-none',
        isCancelled && 'opacity-50',
      )}
    >
      {/* Left: date */}
      <div className={cn(
        'w-[72px] flex-shrink-0 flex flex-col items-center justify-center py-4 rounded-l-2xl',
        today ? 'bg-orange-50' : 'bg-gray-50',
      )}>
        <span className={cn(
          'text-[10px] font-bold uppercase tracking-wide leading-none',
          today ? 'text-orange-500' : 'text-text-secondary',
        )}>
          {format(start, 'EEE', { locale: es })}
        </span>
        <span className={cn(
          'text-[30px] font-black leading-none mt-1 tabular-nums',
          today ? 'text-orange-500' : 'text-text-primary',
        )}>
          {format(start, 'd')}
        </span>
      </div>

      {/* Divider */}
      <div className="w-px bg-gray-200 my-4 flex-shrink-0" />

      {/* Middle: time + location */}
      <div className="flex flex-col justify-center gap-1.5 py-3 px-3 w-32 sm:w-44 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Clock size={12} className="text-text-secondary flex-shrink-0" />
          <span className="text-xs text-text-secondary tabular-nums">
            {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
          </span>
        </div>
        {location && (
          <div className="flex items-center gap-1.5">
            <MapPin size={12} className="text-text-secondary flex-shrink-0" />
            <span className="text-xs text-text-secondary truncate">{location}</span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="w-px bg-gray-200 my-4 flex-shrink-0" />

      {/* Right: title + avatar */}
      <div className="flex-1 min-w-0 flex items-center gap-2 px-3 py-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate leading-snug flex items-center gap-1">
            {cita?.esRecurrente && (
              <span className="text-[10px] text-text-secondary flex-shrink-0" title="Cita recurrente">↻</span>
            )}
            {ev.title}
          </p>
          {(doctor || cita?.motivo) && (
            <p className="text-[11px] text-text-secondary truncate mt-0.5">
              {[doctor ? `Dr. ${doctor}` : '', cita?.motivo ?? ''].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        {doctor && (
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 ring-2 ring-white"
            style={{ backgroundColor: ev.color ?? '#0c77a6' }}
            title={`Dr. ${doctor}`}
          >
            {initials(doctor)}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Month divider ────────────────────────────────────────────────────────── */

function MonthDivider({ date }: { date: Date }) {
  return (
    <div className="px-4 pt-5 pb-1">
      <p className="text-base font-bold text-text-primary capitalize">
        {format(date, "MMMM yyyy", { locale: es })}
      </p>
    </div>
  );
}

/* ── Main ─────────────────────────────────────────────────────────────────── */

interface ListViewProps {
  date: Date;
  events: CalendarEvent[];
  onEventClick?: (e: CalendarEvent) => void;
}

export function ListView({ events, onEventClick }: ListViewProps) {
  const [tab, setTab] = useState<Tab>('upcoming');

  const filtered = useMemo(() => filterByTab(events, tab), [events, tab]);

  const nodes = useMemo(() => {
    const result: ReactNode[] = [];
    filtered.forEach((ev, i) => {
      const evDate  = new Date(ev.start);
      const prevDate = i > 0 ? new Date(filtered[i - 1].start) : null;
      if (!prevDate || !isSameMonth(prevDate, evDate)) {
        result.push(<MonthDivider key={`month-${evDate.getMonth()}-${evDate.getFullYear()}`} date={evDate} />);
      }
      result.push(<EventCard key={ev.id} ev={ev} onEventClick={onEventClick} />);
    });
    return result;
  }, [filtered, onEventClick]);

  return (
    <div className="flex flex-col h-full bg-gray-50/40">

      {/* Filter tabs — mobile select */}
      <div className="md:hidden flex-shrink-0 relative px-3 py-2.5 bg-white border-b border-gray-100">
        <select
          value={tab}
          onChange={e => setTab(e.target.value as Tab)}
          className="w-full appearance-none px-4 py-2.5 pr-10 rounded-xl border border-gray-200 bg-white text-sm font-semibold text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          {TABS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <ChevronDown size={16} className="absolute right-6 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
      </div>
      {/* Filter tabs — desktop underline bar */}
      <div className="hidden md:flex items-center border-b border-gray-100 bg-white flex-shrink-0 px-2 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'px-3 py-3 text-xs font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors',
              tab === t.id
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-text-secondary hover:text-text-primary',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Event list */}
      <div className="flex-1 overflow-y-auto pb-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 text-text-secondary">
            <CalendarDays size={32} className="opacity-25" />
            <p className="text-sm">Sin citas en esta categoría</p>
          </div>
        ) : nodes}
      </div>
    </div>
  );
}
